import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useForm } from "@inertiajs/react";
import { Camera, Loader2, Mail } from "lucide-react";
import { useRef } from "react";

type Props = {
  profile: {
    name: string;
    email: string;
    avatar: string | null;
  };
};

export function ProfileSection({ profile }: Props) {
  return (
    <div className="space-y-6">
      <ProfileForm profile={profile} />
      <Separator />
      <PasswordForm />
    </div>
  );
}

function ProfileForm({ profile }: Props) {
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const form = useForm({
    section: "profile",
    name: profile.name,
    email_address: profile.email,
    avatar: null as File | null,
    current_password: "",
  });

  const emailChanged =
    form.data.email_address.trim().toLowerCase() !== profile.email;

  const avatarPreview = form.data.avatar
    ? URL.createObjectURL(form.data.avatar)
    : profile.avatar;

  const initials = profile.name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    form.put("/app/settings", {
      forceFormData: true,
    });
  }

  function handleAvatarChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) {
      form.setData("avatar", file);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Perfil</CardTitle>
        <CardDescription>
          Atualize suas informações pessoais e foto de perfil.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Avatar */}
          <div className="flex items-center gap-4">
            <div className="relative group">
              <Avatar className="size-16">
                <AvatarImage src={avatarPreview ?? undefined} />
                <AvatarFallback className="text-lg">{initials}</AvatarFallback>
              </Avatar>
              <button
                type="button"
                onClick={() => avatarInputRef.current?.click()}
                className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 transition-opacity group-hover:opacity-100"
              >
                <Camera className="size-4 text-white" />
              </button>
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleAvatarChange}
              />
            </div>
            <div className="text-sm">
              <p className="font-medium">{profile.name}</p>
              <p className="text-muted-foreground">{profile.email}</p>
            </div>
          </div>

          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Nome</Label>
            <Input
              id="name"
              value={form.data.name}
              onChange={(e) => form.setData("name", e.target.value)}
            />
            {form.errors.name && (
              <p className="text-destructive text-sm">{form.errors.name}</p>
            )}
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email">E-mail</Label>
            <Input
              id="email"
              type="email"
              value={form.data.email_address}
              onChange={(e) => form.setData("email_address", e.target.value)}
            />
            {form.errors.email_address && (
              <p className="text-destructive text-sm">
                {form.errors.email_address}
              </p>
            )}
          </div>

          {/* Current password — required when email is changed */}
          {emailChanged && (
            <div className="space-y-2">
              <div className="rounded-md border border-blue-200 bg-blue-50 p-3 dark:border-blue-900 dark:bg-blue-950">
                <p className="text-muted-foreground flex items-center gap-2 text-sm">
                  <Mail className="size-4 shrink-0" />
                  Um e-mail de confirmação será enviado para o novo endereço.
                </p>
              </div>
              <Label htmlFor="profile_current_password">
                Senha atual <span className="text-destructive">*</span>
              </Label>
              <Input
                id="profile_current_password"
                type="password"
                value={form.data.current_password}
                onChange={(e) =>
                  form.setData("current_password", e.target.value)
                }
                placeholder="Digite sua senha para confirmar"
              />
              {form.errors.current_password && (
                <p className="text-destructive text-sm">
                  {form.errors.current_password}
                </p>
              )}
            </div>
          )}

          <div className="flex justify-end">
            <Button type="submit" disabled={form.processing}>
              {form.processing && (
                <Loader2 className="mr-2 size-4 animate-spin" />
              )}
              Salvar alterações
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function PasswordForm() {
  const form = useForm({
    section: "password",
    current_password: "",
    password: "",
    password_confirmation: "",
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    form.put("/app/settings", {
      onSuccess: () => form.reset(),
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Alterar senha</CardTitle>
        <CardDescription>
          Atualize sua senha para manter sua conta segura.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="current_password">Senha atual</Label>
            <Input
              id="current_password"
              type="password"
              value={form.data.current_password}
              onChange={(e) =>
                form.setData("current_password", e.target.value)
              }
            />
            {form.errors.current_password && (
              <p className="text-destructive text-sm">
                {form.errors.current_password}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Nova senha</Label>
            <Input
              id="password"
              type="password"
              value={form.data.password}
              onChange={(e) => form.setData("password", e.target.value)}
            />
            {form.errors.password && (
              <p className="text-destructive text-sm">{form.errors.password}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password_confirmation">Confirmar nova senha</Label>
            <Input
              id="password_confirmation"
              type="password"
              value={form.data.password_confirmation}
              onChange={(e) =>
                form.setData("password_confirmation", e.target.value)
              }
            />
            {form.errors.password_confirmation && (
              <p className="text-destructive text-sm">
                {form.errors.password_confirmation}
              </p>
            )}
          </div>

          <div className="flex justify-end">
            <Button type="submit" disabled={form.processing}>
              {form.processing && (
                <Loader2 className="mr-2 size-4 animate-spin" />
              )}
              Alterar senha
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
